import { useCallback, useId, useMemo, useState } from "react";
import Search from "@mui/icons-material/Search";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormLabel,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { debug as debugFn } from "debug";
import { ok } from "neverthrow";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { v4 } from "uuid";

import { mustBeDefined, mustParse } from "@thalamos/common";
import type {
  ExtendedOrganisation,
  OrganisationId,
} from "@thalamos/vision-common";
import {
  ExtendedOrganisationArraySchema,
  OrganisationIdSchema,
} from "@thalamos/vision-common";

import { apiHooks } from "../../../api/index.js";
import { createRoute } from "../../../routing/createRoute.js";

const debug = debugFn("vision-frontend:OrganisationsManagement");

function OrganisationRow({
  organisation,
  onClick,
}: {
  organisation: ExtendedOrganisation;
  onClick: () => void;
}) {
  return (
    <TableRow key={organisation.id}>
      <TableCell>{organisation.name}</TableCell>
      <TableCell>{organisation.managingOrganisationName ?? "None"}</TableCell>
      <TableCell>{organisation.role}</TableCell>
      <TableCell>
        <Button variant="contained" onClick={onClick}>
          Edit
        </Button>
      </TableCell>
    </TableRow>
  );
}

export const OrganisationsManagementInner = ({
  organisations,
  onClick,
  page,
  setPage,
  numPages,
}: {
  organisations: ExtendedOrganisation[];
  onClick: (organisationId: OrganisationId) => void;
  page: number;
  setPage: (page: number) => void;
  numPages: number;
}) => {
  return (
    <Box>
      <TableContainer>
        <Table size="small" aria-label="a dense table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Managing Org Name</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {organisations
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((organisation) => (
                <OrganisationRow
                  key={organisation.id}
                  organisation={organisation}
                  onClick={() => {
                    onClick(organisation.id);
                  }}
                />
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          paddingTop: "3em",
        }}
      >
        <button
          disabled={page === 0}
          onClick={() => {
            if (page === 0) {
              return;
            }
            setPage(page - 1);
          }}
        >
          &#60;
        </button>
        <Box sx={{ marginLeft: "1em", marginRight: "1em" }}>
          Page {page + 1} of {numPages}
        </Box>
        <button
          disabled={page >= numPages - 1}
          onClick={() => {
            if (page < numPages - 1) {
              setPage(page + 1);
            }
          }}
        >
          &#62;
        </button>
      </Box>
    </Box>
  );
};
export function OrganisationsManagement() {
  const navigate = useNavigate();
  const [orgFilterQuery, setOrgFilterQuery] = useState("");
  const [page, setPage] = useState(0);
  const orgPageSize = 20;

  const [{ data, loading }] = apiHooks.organisation.search(
    orgFilterQuery,
    orgPageSize,
    page * orgPageSize,
  );

  const numPages = data && data.count ? Math.ceil(data.count / orgPageSize) : 1;

  const organisations = useMemo(
    () =>
      loading
        ? []
        : ok(data?.results)
            .andThen(mustBeDefined())
            .andThen(mustParse(ExtendedOrganisationArraySchema))
            .orTee(debug)
            .unwrapOr([]),
    [data?.results, loading],
  );

  const onClick = useCallback(
    (organisationId: OrganisationId) => {
      navigate(createRoute.adminOrganisationManagement(organisationId));
    },
    [navigate],
  );

  const { t } = useTranslation();
  const ariaId = useId();

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          marginTop: "1em",
          marginBottom: "1em",
          justifyContent: "space-between",
        }}
      >
        <FormControl sx={{ marginBottom: "1em", marginLeft: "1em" }}>
          <FormLabel
            htmlFor={ariaId}
            sx={{
              marginBottom: "0.5em",
              fontWeight: "bold",
              color: "text.primary",
            }}
          >
            {t("admin.organisations.searchLabel")}
          </FormLabel>
          <TextField
            id={ariaId}
            value={orgFilterQuery}
            onChange={(e) => {
              setOrgFilterQuery(e.target.value);
              setPage(0);
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              },
            }}
          />
        </FormControl>
        <Button
          variant="contained"
          onClick={() => {
            const newOrgId = OrganisationIdSchema.parse(v4());
            navigate(createRoute.adminOrganisationManagement(newOrgId));
          }}
          sx={{
            height: "3em",
            alignSelf: "center",
            marginRight: "3em",
            width: "15em",
          }}
        >
          {t("admin.organisations.add")}
        </Button>
      </Box>
      {loading ? (
        <CircularProgress />
      ) : (
        <OrganisationsManagementInner
          organisations={organisations}
          onClick={onClick}
          page={page}
          setPage={setPage}
          numPages={numPages}
        />
      )}
    </Box>
  );
}
